import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { Card, ErrorNote, Field, Loading, PageHead, Pill, useResource, useToast } from '../components/ui';
import { FormPreview } from '../components/FormPreview';

/**
 * CM-3b — the form builder.
 *
 * Three panes, because that is what the job is: what you can add, what you have
 * added, and the settings of the one thing you are editing. The right-hand pane
 * changes with the selected field rather than opening a dialog, so an operator
 * can see the whole form while they tune one row of it.
 */

const FIELD_KINDS = [
  ['single_line', 'Single line', 'Short text.'],
  ['paragraph', 'Paragraph', 'Multi-line text.'],
  ['email', 'Email', 'Validated email address.'],
  ['phone', 'Phone number', 'Validated Indian mobile number.'],
  ['date', 'Date', 'Calendar date.'],
  ['dropdown', 'Dropdown', 'Single selection from a defined list.'],
  ['checkbox', 'Consent checkbox', 'Records consent against one purpose.'],
  ['jurisdiction', 'Jurisdiction', 'Controls conditional display of other fields.'],
  ['heading', 'Heading', 'Section title. No data collected.'],
] as const;

type Kind = (typeof FIELD_KINDS)[number][0];

interface FormField {
  key: string;
  kind: Kind;
  label: string;
  help: string;
  placeholder: string;
  required: boolean;
  showLabel: boolean;
  options: Array<{ value: string; label: string }>;
  validation: { minLength?: number; maxLength?: number; pattern?: string; message?: string };
  identifier: 'email' | 'phone' | 'customer_id' | null;
  purposeId: string | null;
  showForJurisdictions: string[];
}

interface Definition {
  fields: FormField[];
  settings: {
    submitText: string; resetText: string; showReset: boolean; successMessage: string;
    languageDetection: boolean; captcha: 'none' | 'host';
    defaultLang: string; verify: 'none' | 'email' | 'sms'; onRequiredDeclined: 'record' | 'refuse';
  };
  styling: {
    buttonBackground: string; buttonText: string;
    resetBackground: string; resetText: string; accent: string;
  };
}

interface VersionRow {
  formId: string; version: number; definition: Definition;
  contentHash: string; status: 'draft' | 'published' | 'retired'; publishedAt: string | null;
}

interface FormRow {
  id: string; key: string; name: string; description: string;
  status: string; liveVersion: number | null; collectionPointId: string;
}

interface Purpose {
  id: string; key: string;
  current: { name: string; description: string; basis: string; requiredForService: boolean };
  status: string;
}

interface CollectionPointRow {
  id: string; name: string; channel: string; noticeId: string; purposeIds: string[];
  apiKeyPrefix: string; consentMaxAgeDays: number | null;
}

interface Loaded {
  form: FormRow;
  versions: VersionRow[];
  current: VersionRow;
  variants: Array<{ lang: string; strings: VariantStrings; contentHash: string }>;
}

interface VariantStrings {
  fields: Record<string, { label?: string; help?: string; placeholder?: string; options?: Record<string, string>; validationMessage?: string }>;
  settings: { submitText?: string; resetText?: string; successMessage?: string };
  errors: Record<string, string>;
}

function blankField(kind: Kind, n: number): FormField {
  return {
    key: `${kind}_${n}`,
    kind,
    label: FIELD_KINDS.find(([k]) => k === kind)?.[1] ?? 'Field',
    help: '', placeholder: '', required: false, showLabel: kind !== 'heading',
    options: kind === 'dropdown' || kind === 'jurisdiction'
      ? [{ value: 'India', label: 'India' }]
      : [],
    validation: {},
    identifier: null,
    purposeId: null,
    showForJurisdictions: [],
  };
}

type Tab = 'setup' | 'fields' | 'languages' | 'rules' | 'preview' | 'publish';

export default function FormBuilder() {
  const { formId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const user = getUser();
  const toast = useToast();
  const writes = can(user, 'forms:edit');

  const wantVersion = params.get('version');
  const [reload, setReload] = useState(0);
  const loaded = useResource<Loaded>(
    () => api.get(`/v1/forms/${formId}${wantVersion ? `?version=${wantVersion}` : ''}`),
    [formId, reload, wantVersion],
  );
  const purposes = useResource<{ purposes: Purpose[] }>(() => api.get('/v1/purposes'));
  const cpId = loaded.data?.form.collectionPointId;
  const point = useResource<{ collectionPoint: CollectionPointRow } | null>(
    () => (cpId ? api.get(`/v1/collection-points/${cpId}`) : Promise.resolve(null)),
    [cpId],
  );
  const languages = useResource<{ languages: string[] }>(() => api.get('/v1/settings/languages'));

  const [tab, setTab] = useState<Tab>((params.get('tab') as Tab) || 'fields');
  const [previewLang, setPreviewLang] = useState('en');
  const [draft, setDraft] = useState<Definition | null>(null);
  const [meta, setMeta] = useState<{ name: string; description: string } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const version = loaded.data?.current;
  const editable = writes && version?.status === 'draft';

  useEffect(() => {
    if (version) {
      setDraft(structuredClone(version.definition));
      setSelected(version.definition.fields[0]?.key ?? null);
      setDirty(false);
    }
    if (loaded.data) setMeta({ name: loaded.data.form.name, description: loaded.data.form.description });
  }, [version?.formId, version?.version, version?.contentHash, loaded.data?.form.name, loaded.data?.form.description]);

  function edit(next: Definition) {
    setDraft(next);
    setDirty(true);
  }
  function editMeta(next: { name: string; description: string }) {
    setMeta(next);
    setDirty(true);
  }

  async function save() {
    if (!draft || !version) return;
    setSaving(true);
    setError(null);
    try {
      if (meta && (meta.name !== loaded.data?.form.name || meta.description !== loaded.data?.form.description)) {
        await api.put(`/v1/forms/${formId}`, meta);
      }
      await api.put(`/v1/forms/${formId}/versions/${version.version}`, draft);
      setDirty(false);
      toast('Saved.', 'ok');
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // A link straight to a tab (from Rules to Fields, or a bookmark) lands on it.
  useEffect(() => {
    const t = params.get('tab') as Tab | null;
    if (t && t !== tab) setTab(t);
  }, [params]);

  function pickTab(t: Tab) {
    setTab(t);
    const next = new URLSearchParams(params);
    next.set('tab', t);
    setParams(next, { replace: true });
  }

  if (loaded.loading && !loaded.data) return <Loading what="Loading the form" />;
  if (loaded.error) return <ErrorNote error={loaded.error} />;
  if (!loaded.data || !draft || !version) return null;

  const { form } = loaded.data;
  const cp = point.data?.collectionPoint ?? null;
  const offered = languages.data?.languages ?? ['en'];
  const draftRow = loaded.data.versions.find((v) => v.status === 'draft');

  return (
    <>
      <PageHead eyebrow="Consent forms" backTo="/forms" title={meta?.name || form.name}>
        <select
          aria-label="Version"
          value={version.version}
          style={{ width: 'auto' }}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            next.set('version', e.target.value);
            setParams(next);
          }}
        >
          {loaded.data.versions.map((v) => (
            <option key={v.version} value={v.version}>
              Version {v.version} · {v.status === 'published' ? 'live' : v.status}
            </option>
          ))}
        </select>
        {version.status === 'published' ? <Pill tone="ok">Live</Pill> : version.status === 'draft' ? <Pill tone="warn">Draft</Pill> : <Pill>Retired</Pill>}
        {editable && (
          <button className="primary" onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        )}
        {writes && version.status !== 'draft' && (
          draftRow ? (
            <button onClick={() => { const next = new URLSearchParams(params); next.set('version', String(draftRow.version)); setParams(next); }}>
              Open draft v{draftRow.version}
            </button>
          ) : (
            <button
              onClick={async () => {
                try {
                  const res = await api.post<{ version: { version: number } }>(`/v1/forms/${formId}/versions`);
                  const next = new URLSearchParams(params);
                  next.set('version', String(res.version.version));
                  setParams(next);
                  toast(`Draft v${res.version.version} opened from v${version.version}.`, 'ok');
                } catch (err) {
                  toast((err as Error).message, 'bad');
                }
              }}
            >
              New version
            </button>
          )
        )}
      </PageHead>

      <ErrorNote error={error} />

      <div className="tabs">
        {([
          ['setup', 'Setup'], ['fields', 'Fields'], ['languages', 'Languages'],
          ['rules', 'Rules'], ['preview', 'Preview'], ['publish', 'Publish'],
        ] as Array<[Tab, string]>).map(([t, label]) => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => pickTab(t)}>{label}</button>
        ))}
      </div>

      {tab === 'setup' && meta && (
        <SetupTab
          form={form} meta={meta} draft={draft} editable={!!editable} point={cp} offered={offered}
          onMeta={editMeta} onChange={edit}
        />
      )}

      {tab === 'fields' && (
        <FieldsTab
          draft={draft}
          editable={!!editable}
          purposes={purposes.data?.purposes ?? []}
          point={cp}
          selected={selected}
          onSelect={setSelected}
          onChange={edit}
        />
      )}

      {tab === 'languages' && (
        <LanguagesTab
          formId={formId}
          version={version}
          draft={draft}
          variants={loaded.data.variants}
          offered={offered}
          editable={!!editable}
          onSaved={() => setReload((n) => n + 1)}
        />
      )}

      {tab === 'rules' && (
        <RulesTab draft={draft} editable={!!editable} purposes={purposes.data?.purposes ?? []} point={cp} onChange={edit} onSelectField={(k) => { setSelected(k); pickTab('fields'); }} />
      )}

      {tab === 'preview' && (
        <Card title="Preview">
          {dirty ? (
            <div className="note warn">Unsaved changes. The preview shows the saved draft.</div>
          ) : null}
          <FormPreview
            formId={formId}
            version={version.version}
            lang={previewLang}
            onLangChange={setPreviewLang}
          />
        </Card>
      )}

      {tab === 'publish' && (
        <PublishTab
          formId={formId}
          form={form}
          version={version}
          dirty={dirty}
          writes={writes}
          onPublished={() => setReload((n) => n + 1)}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------ setup

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Website', app: 'Mobile app', api: 'System integration', offline: 'Paper or in person',
  callcentre: 'Call centre', cookie: 'Cookie banner',
  paper: 'Paper form', kiosk: 'Kiosk', ivr: 'Phone (IVR)', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp',
};

function SetupTab({
  form, meta, draft, editable, point, offered, onMeta, onChange,
}: {
  form: FormRow;
  meta: { name: string; description: string };
  draft: Definition;
  editable: boolean;
  point: CollectionPointRow | null;
  offered: string[];
  onMeta: (m: { name: string; description: string }) => void;
  onChange: (d: Definition) => void;
}) {
  const set = (next: Partial<Definition['settings']>) =>
    onChange({ ...draft, settings: { ...draft.settings, ...next } });
  const style = (next: Partial<Definition['styling']>) =>
    onChange({ ...draft, styling: { ...draft.styling, ...next } });

  return (
    <div className="grid cols-2" style={{ alignItems: 'start' }}>
      <div>
        <Card title="Form">
          <Field label="Name">
            <input type="text" value={meta.name} disabled={!editable} onChange={(e) => onMeta({ ...meta, name: e.target.value })} />
          </Field>
          <Field label="Description" help="Internal.">
            <textarea value={meta.description} disabled={!editable} onChange={(e) => onMeta({ ...meta, description: e.target.value })} />
          </Field>
          <table className="data">
            <tbody>
              <tr><td className="small muted" style={{ width: 160 }}>Collection point</td><td>{point ? <Link to="/collection-points">{point.name}</Link> : '…'}</td></tr>
              <tr><td className="small muted">Channel</td><td>{point ? (CHANNEL_LABEL[point.channel] ?? point.channel) : '…'}</td></tr>
              <tr><td className="small muted">Notice shown</td><td>{point ? <Link to={`/notices/${point.noticeId}/versions/1`}>The published notice for this point</Link> : '…'}</td></tr>
              <tr><td className="small muted">Purposes asked</td><td>{point ? point.purposeIds.length : '…'}</td></tr>
              <tr>
                <td className="small muted">Consent kept for</td>
                <td>{point ? (point.consentMaxAgeDays ? `${point.consentMaxAgeDays} days, then asked again` : 'Each purpose\'s own retention period') : '…'}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card title="Language">
          <Field label="Shown first" help="Used when the browser gives no language, or when detection is off.">
            <select value={draft.settings.defaultLang} disabled={!editable} onChange={(e) => set({ defaultLang: e.target.value })}>
              {offered.map((c) => <option key={c} value={c}>{EIGHTH_SCHEDULE.find(([code]) => code === c)?.[1] ?? c}</option>)}
            </select>
          </Field>
          <label className="fb-check">
            <input type="checkbox" checked={draft.settings.languageDetection} disabled={!editable}
              onChange={(e) => set({ languageDetection: e.target.checked })} />
            Follow the visitor's browser language when the form has it
          </label>
        </Card>
      </div>

      <div>
        <Card title="Buttons">
          <div className="row">
            <Field label="Submit button">
              <input type="text" value={draft.settings.submitText} disabled={!editable} onChange={(e) => set({ submitText: e.target.value })} />
            </Field>
            <Field label="Reset button" help={draft.settings.showReset ? undefined : 'Hidden.'}>
              <input type="text" value={draft.settings.resetText} disabled={!editable || !draft.settings.showReset} onChange={(e) => set({ resetText: e.target.value })} />
            </Field>
          </div>
          <label className="fb-check">
            <input type="checkbox" checked={draft.settings.showReset} disabled={!editable} onChange={(e) => set({ showReset: e.target.checked })} />
            Show a reset button
          </label>
          <Field label="After submitting" help="Shown once the choices are recorded.">
            <input type="text" value={draft.settings.successMessage} disabled={!editable} onChange={(e) => set({ successMessage: e.target.value })} />
          </Field>
          <Field label="CAPTCHA" help="Host page: your page runs its own check before submit. No third-party CAPTCHA is embedded.">
            <select value={draft.settings.captcha} disabled={!editable} onChange={(e) => set({ captcha: e.target.value as 'none' | 'host' })}>
              <option value="none">None</option>
              <option value="host">Host page</option>
            </select>
          </Field>
        </Card>

        <Card title="Colours">
          <div className="row">
            <Field label="Submit background">
              <input type="color" value={draft.styling.buttonBackground} disabled={!editable} onChange={(e) => style({ buttonBackground: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Submit text">
              <input type="color" value={draft.styling.buttonText} disabled={!editable} onChange={(e) => style({ buttonText: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Accent">
              <input type="color" value={draft.styling.accent} disabled={!editable} onChange={(e) => style({ accent: e.target.value.toUpperCase() })} />
            </Field>
          </div>
          <div className="row">
            <Field label="Reset background">
              <input type="color" value={draft.styling.resetBackground} disabled={!editable} onChange={(e) => style({ resetBackground: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Reset text">
              <input type="color" value={draft.styling.resetText} disabled={!editable} onChange={(e) => style({ resetText: e.target.value.toUpperCase() })} />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ rules

function RulesTab({
  draft, editable, purposes, point, onChange, onSelectField,
}: {
  draft: Definition;
  editable: boolean;
  purposes: Purpose[];
  point: CollectionPointRow | null;
  onChange: (d: Definition) => void;
  onSelectField: (key: string) => void;
}) {
  const set = (next: Partial<Definition['settings']>) =>
    onChange({ ...draft, settings: { ...draft.settings, ...next } });
  const identifier = draft.fields.find((f) => f.identifier);
  const jurisdiction = draft.fields.find((f) => f.kind === 'jurisdiction');
  const gated = draft.fields.filter((f) => f.showForJurisdictions.length);
  const required = (point?.purposeIds ?? [])
    .map((id) => purposes.find((p) => p.id === id))
    .filter((p): p is Purpose => !!p && p.current.basis === 'consent' && p.current.requiredForService);
  const verifyMismatch = draft.settings.verify === 'email' ? identifier?.identifier !== 'email'
    : draft.settings.verify === 'sms' ? identifier?.identifier !== 'phone' : false;

  return (
    <div className="grid cols-2" style={{ alignItems: 'start' }}>
      <div>
        <Card title="Verification">
          <Field label="Send a code by">
            <select value={draft.settings.verify} disabled={!editable} onChange={(e) => set({ verify: e.target.value as Definition['settings']['verify'] })}>
              <option value="none">Do not send a code</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </Field>
          {draft.settings.verify !== 'none' && (
            identifier
              ? verifyMismatch
                ? <div className="field-error">The identifier field "{identifier.label}" is {identifier.identifier === 'email' ? 'an email address' : identifier.identifier === 'phone' ? 'a mobile number' : 'a customer ID'}; a code by {draft.settings.verify === 'email' ? 'email' : 'SMS'} needs {draft.settings.verify === 'email' ? 'an email address' : 'a mobile number'}. <button className="small" type="button" onClick={() => onSelectField(identifier.key)}>Open the field</button></div>
                : <div className="note ok">The code goes to "{identifier.label}".</div>
              : <div className="field-error">No field is marked as the identifier yet.</div>
          )}
        </Card>

        <Card title="Required purpose declined">
          <Field label="Action" help="Optional purposes are never affected (S.6(1)).">
            <select value={draft.settings.onRequiredDeclined} disabled={!editable} onChange={(e) => set({ onRequiredDeclined: e.target.value as 'record' | 'refuse' })}>
              <option value="record">Record the decline and let your page decide</option>
              <option value="refuse">Refuse the submission and name the purpose</option>
            </select>
          </Field>
          {required.length
            ? <div className="small">On this form: {required.map((p) => p.current.name).join(', ')}.</div>
            : <div className="small muted">No required purpose on this collection point.</div>}
        </Card>
      </div>

      <div>
        <Card title="Jurisdiction">
          {jurisdiction ? (
            <>
              <div className="small">
                The person picks from "{jurisdiction.label}": {jurisdiction.options.map((o) => o.label).join(', ') || 'no options yet'}.
              </div>
              <table className="data" style={{ marginTop: 10 }}>
                <thead><tr><th>Field</th><th>Shown for</th><th /></tr></thead>
                <tbody>
                  {draft.fields.filter((f) => f.kind !== 'jurisdiction').map((f) => (
                    <tr key={f.key}>
                      <td>{f.label}</td>
                      <td className="small">{f.showForJurisdictions.length ? f.showForJurisdictions.join(', ') : <span className="muted">everyone</span>}</td>
                      <td style={{ textAlign: 'right' }}><button className="small" type="button" onClick={() => onSelectField(f.key)}>Change</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="small muted">
              No jurisdiction field.
              {gated.length > 0 && <div className="field-error" style={{ marginTop: 6 }}>{gated.length} field{gated.length === 1 ? ' is' : 's are'} limited by jurisdiction but there is nothing to decide it.</div>}
            </div>
          )}
        </Card>

        <Card title="Consent validity">
          <div className="small">
            {point?.consentMaxAgeDays
              ? `Consent taken here is asked again after ${point.consentMaxAgeDays} days.`
              : 'Per purpose retention period.'}
          </div>
          <table className="data" style={{ marginTop: 10 }}>
            <tbody>
              {(point?.purposeIds ?? []).map((id) => purposes.find((p) => p.id === id)).filter((p): p is Purpose => !!p).map((p) => (
                <tr key={p.id}><td>{p.current.name}</td><td className="small muted">{p.current.basis === 'consent' ? 'Consent' : 'Stated, no consent taken'}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="small muted" style={{ marginTop: 8 }}><Link to="/purposes">Purposes</Link></div>
        </Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ fields

/**
 * What the publish check will say, computed as you type, so a row shows its
 * problem the moment it has one instead of at the end.
 */
function rowProblems(draft: Definition, purposes: Purpose[], point: CollectionPointRow | null): Record<string, string> {
  const out: Record<string, string> = {};
  const identifiers = draft.fields.filter((f) => f.identifier);
  const seen = new Map<string, number>();
  for (const f of draft.fields) {
    if (!f.label.trim()) out[f.key] = 'Needs a label.';
    if (f.kind === 'checkbox' && !f.purposeId) out[f.key] = 'Choose the purpose this checkbox records.';
    if (f.kind === 'checkbox' && f.purposeId) {
      seen.set(f.purposeId, (seen.get(f.purposeId) ?? 0) + 1);
      const p = purposes.find((x) => x.id === f.purposeId);
      if (p && p.current.basis !== 'consent') out[f.key] = `"${p.current.name}" is stated under S.7, not asked. Remove this checkbox.`;
      if (point && !point.purposeIds.includes(f.purposeId)) out[f.key] = 'This purpose is not offered at this collection point.';
    }
    if ((f.kind === 'dropdown' || f.kind === 'jurisdiction') && !f.options.length) out[f.key] = 'Add at least one option.';
    if (f.identifier && !f.required) out[f.key] = 'The identifier must be required.';
  }
  for (const [id, n] of seen) {
    if (n > 1) for (const f of draft.fields) if (f.purposeId === id) out[f.key] = 'Two checkboxes record the same purpose.';
  }
  if (identifiers.length > 1) for (const f of identifiers) out[f.key] = 'Only one field can be the identifier.';
  return out;
}

function FieldsTab({
  draft, editable, purposes, point, selected, onSelect, onChange,
}: {
  draft: Definition;
  editable: boolean;
  purposes: Purpose[];
  point: CollectionPointRow | null;
  selected: string | null;
  onSelect: (key: string | null) => void;
  onChange: (d: Definition) => void;
}) {
  const field = draft.fields.find((f) => f.key === selected) ?? null;
  const problems = rowProblems(draft, purposes, point);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const askable = (point?.purposeIds ?? [])
    .map((id) => purposes.find((p) => p.id === id))
    .filter((p): p is Purpose => !!p && p.current.basis === 'consent' && p.status === 'active');
  const uncovered = askable.filter((p) => !draft.fields.some((f) => f.kind === 'checkbox' && f.purposeId === p.id));
  const hasIdentifier = draft.fields.some((f) => f.identifier);

  function addAllPurposes() {
    let n = draft.fields.length;
    const added = uncovered.map((p) => ({
      ...blankField('checkbox', ++n),
      key: p.key.replace(/[^a-z0-9_]/g, '_').slice(0, 48) || `consent_${n}`,
      label: p.current.name,
      help: p.current.description,
      purposeId: p.id,
    }));
    onChange({ ...draft, fields: [...draft.fields, ...added] });
    if (added[0]) onSelect(added[0].key);
  }

  function addIdentifier() {
    const f = { ...blankField('email', draft.fields.length + 1), key: 'email', label: 'Email address', placeholder: 'you@example.com', required: true, identifier: 'email' as const };
    onChange({ ...draft, fields: [f, ...draft.fields] });
    onSelect(f.key);
  }

  function dropOn(targetKey: string) {
    if (!dragging || dragging === targetKey) return;
    const fields = [...draft.fields];
    const from = fields.findIndex((f) => f.key === dragging);
    const to = fields.findIndex((f) => f.key === targetKey);
    if (from < 0 || to < 0) return;
    const [moved] = fields.splice(from, 1);
    fields.splice(to, 0, moved!);
    onChange({ ...draft, fields });
  }

  function replace(key: string, next: Partial<FormField>) {
    onChange({ ...draft, fields: draft.fields.map((f) => (f.key === key ? { ...f, ...next } : f)) });
  }

  function add(kind: Kind) {
    const f = blankField(kind, draft.fields.length + 1);
    onChange({ ...draft, fields: [...draft.fields, f] });
    onSelect(f.key);
  }

  function move(key: string, by: number) {
    const i = draft.fields.findIndex((f) => f.key === key);
    const j = i + by;
    if (i < 0 || j < 0 || j >= draft.fields.length) return;
    const fields = [...draft.fields];
    [fields[i], fields[j]] = [fields[j]!, fields[i]!];
    onChange({ ...draft, fields });
  }

  function remove(key: string) {
    onChange({ ...draft, fields: draft.fields.filter((f) => f.key !== key) });
    if (selected === key) onSelect(null);
  }

  return (
    <div className="fb">
      <Card title="Add a field">
        <div className="fb-toolbox">
          {FIELD_KINDS.map(([kind, title, help]) => (
            <button key={kind} className="fb-tool" disabled={!editable} onClick={() => add(kind)} title={help}>
              <strong>{title}</strong>
              <span className="small muted">{help}</span>
            </button>
          ))}
        </div>
      </Card>

      <Card title={`Fields (${draft.fields.length})`}>
        {editable && (!hasIdentifier || uncovered.length > 0) && (
          <div className="note" style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>
              {!hasIdentifier
                ? 'No identifier field. One field must identify the person.'
                : `${uncovered.length} purpose${uncovered.length === 1 ? '' : 's'} at this collection point ${uncovered.length === 1 ? 'has' : 'have'} no checkbox yet. A submission with an unanswered purpose is rejected.`}
            </span>
            {!hasIdentifier
              ? <button className="small" type="button" onClick={addIdentifier}>Add an email field as identifier</button>
              : <button className="small" type="button" onClick={addAllPurposes}>Add a checkbox for each</button>}
          </div>
        )}
        {!draft.fields.length && !editable && <div className="small muted">No fields.</div>}
        <div className="fb-list">
          {draft.fields.map((f, i) => {
            const purpose = purposes.find((p) => p.id === f.purposeId);
            const problem = problems[f.key];
            return (
              <div
                key={f.key}
                className={`fb-row${f.key === selected ? ' fb-row-on' : ''}${problem ? ' fb-row-bad' : ''}${over === f.key && dragging && dragging !== f.key ? ' fb-row-over' : ''}`}
                onClick={() => onSelect(f.key)}
                draggable={editable}
                onDragStart={() => setDragging(f.key)}
                onDragEnd={() => { setDragging(null); setOver(null); }}
                onDragOver={(e) => { if (editable) { e.preventDefault(); setOver(f.key); } }}
                onDrop={(e) => { e.preventDefault(); dropOn(f.key); setDragging(null); setOver(null); }}
                title={editable ? 'Drag to reorder' : undefined}
              >
                <div className="fb-row-main">
                  <span className="fb-kind">{FIELD_KINDS.find(([k]) => k === f.kind)?.[1]}</span>
                  <strong>{f.label || <em className="muted">no label yet</em>}</strong>
                  {f.kind === 'checkbox' && purpose && purpose.current.name !== f.label && (
                    <div className="small muted">Records: {purpose.current.name}</div>
                  )}
                  {problem && <div className="field-error">{problem}</div>}
                </div>
                <div className="fb-row-tags">
                  {f.identifier && <Pill tone="brand">Identifier</Pill>}
                  {f.required && <Pill>Required</Pill>}
                  {!!f.showForJurisdictions.length && <Pill tag>Only {f.showForJurisdictions.join(', ')}</Pill>}
                </div>
                {editable && (
                  <div className="fb-row-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="small" disabled={i === 0} onClick={() => move(f.key, -1)} aria-label="Move up">↑</button>
                    <button className="small" disabled={i === draft.fields.length - 1} onClick={() => move(f.key, 1)} aria-label="Move down">↓</button>
                    <button className="small danger" onClick={() => remove(f.key)} aria-label="Remove">×</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card title={field ? `${FIELD_KINDS.find(([k]) => k === field.kind)?.[1] ?? 'Field'}: ${field.label || 'untitled'}` : 'Field settings'}>
        {field
          ? <FieldEditor
              field={field}
              editable={editable}
              purposes={purposes.filter((p) => !point || point.purposeIds.includes(p.id))}
              definition={draft}
              onChange={(next) => replace(field.key, next)}
            />
          : <div className="small muted">Select a field to edit it. Buttons, colours and messages are under Setup.</div>}
      </Card>
    </div>
  );
}

function FieldEditor({
  field, editable, purposes, definition, onChange,
}: {
  field: FormField;
  editable: boolean;
  purposes: Purpose[];
  definition: Definition;
  onChange: (next: Partial<FormField>) => void;
}) {
  const [pane, setPane] = useState<'display' | 'validation' | 'settings'>('display');
  const jurisdictionField = definition.fields.find((f) => f.kind === 'jurisdiction');
  const active = purposes.filter((p) => p.status === 'active');

  return (
    <>
      <div className="tabs">
        <button className={pane === 'display' ? 'active' : ''} onClick={() => setPane('display')}>Display</button>
        <button className={pane === 'validation' ? 'active' : ''} onClick={() => setPane('validation')}>Validation</button>
        <button className={pane === 'settings' ? 'active' : ''} onClick={() => setPane('settings')}>Settings</button>
      </div>

      {pane === 'display' && (
        <>
          <Field label="Label">
            <input type="text" value={field.label} disabled={!editable}
              onChange={(e) => onChange({ label: e.target.value })} />
          </Field>
          {field.kind !== 'heading' && (
            <>
              <Field label="Help text">
                <input type="text" value={field.help} disabled={!editable}
                  onChange={(e) => onChange({ help: e.target.value })} />
              </Field>
              {field.kind !== 'checkbox' && (
                <Field label="Placeholder">
                  <input type="text" value={field.placeholder} disabled={!editable}
                    onChange={(e) => onChange({ placeholder: e.target.value })} />
                </Field>
              )}
              <label className="fb-check">
                <input type="checkbox" checked={field.showLabel} disabled={!editable}
                  onChange={(e) => onChange({ showLabel: e.target.checked })} />
                Show label
              </label>
            </>
          )}

          {(field.kind === 'dropdown' || field.kind === 'jurisdiction') && (
            <Options field={field} editable={editable} onChange={onChange} />
          )}
        </>
      )}

      {pane === 'validation' && (
        field.kind === 'checkbox' || field.kind === 'heading' ? (
          <div className="small muted">
            {field.kind === 'checkbox'
              ? 'Consent checkboxes cannot be required (S.6(1)).'
              : 'Headings collect no data.'}
          </div>
        ) : (
          <>
            <label className="fb-check">
              <input type="checkbox" checked={field.required} disabled={!editable}
                onChange={(e) => onChange({ required: e.target.checked })} />
              Required
            </label>
            <div className="row">
              <Field label="Minimum length">
                <input type="number" min={0} value={field.validation.minLength ?? ''} disabled={!editable}
                  onChange={(e) => onChange({
                    validation: { ...field.validation, minLength: e.target.value ? Number(e.target.value) : undefined },
                  })} />
              </Field>
              <Field label="Maximum length">
                <input type="number" min={1} value={field.validation.maxLength ?? ''} disabled={!editable}
                  onChange={(e) => onChange({
                    validation: { ...field.validation, maxLength: e.target.value ? Number(e.target.value) : undefined },
                  })} />
              </Field>
            </div>
            <Field label="Pattern" help="Regular expression. Optional.">
              <input type="text" value={field.validation.pattern ?? ''} disabled={!editable}
                placeholder="^[A-Za-z ]+$"
                onChange={(e) => onChange({
                  validation: { ...field.validation, pattern: e.target.value || undefined },
                })} />
            </Field>
            <Field label="Validation message" help="Optional. Defaults to standard wording.">
              <input type="text" value={field.validation.message ?? ''} disabled={!editable}
                onChange={(e) => onChange({
                  validation: { ...field.validation, message: e.target.value || undefined },
                })} />
            </Field>
          </>
        )
      )}

      {pane === 'settings' && (
        <>
          <Field label="Name in the data" help="How this answer is keyed in submissions and in the API. Lowercase letters, digits and underscores.">
            <input type="text" value={field.key} disabled={!editable}
              onChange={(e) => onChange({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
          </Field>

          {field.kind === 'checkbox' && (
            <Field
              label="Purpose"
            >
              <select value={field.purposeId ?? ''} disabled={!editable}
                onChange={(e) => onChange({ purposeId: e.target.value || null })}>
                <option value="">Select purpose</option>
                {active.map((p) => (
                  <option key={p.id} value={p.id}>{p.current.name}</option>
                ))}
              </select>
            </Field>
          )}

          {['single_line', 'email', 'phone'].includes(field.kind) && (
            <Field
              label="Identifier"
              help="Exactly one field. Locates the record for lookup, withdrawal and the preference centre."
            >
              <select value={field.identifier ?? ''} disabled={!editable}
                onChange={(e) => onChange({ identifier: (e.target.value || null) as FormField['identifier'] })}>
                <option value="">Not an identifier</option>
                <option value="email">Email address</option>
                <option value="phone">Mobile number</option>
                <option value="customer_id">Customer ID</option>
              </select>
            </Field>
          )}

          {field.kind !== 'jurisdiction' && (
            <Field
              label="Jurisdictions"
              help={jurisdictionField
                ? 'Comma separated. Empty: always shown.'
                : 'Requires a jurisdiction field.'}
            >
              <input
                type="text"
                value={field.showForJurisdictions.join(', ')}
                disabled={!editable || !jurisdictionField}
                placeholder="India, EU"
                onChange={(e) => onChange({
                  showForJurisdictions: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })}
              />
            </Field>
          )}
        </>
      )}
    </>
  );
}

function Options({
  field, editable, onChange,
}: { field: FormField; editable: boolean; onChange: (n: Partial<FormField>) => void }) {
  return (
    <Field label="Options">
      <div className="fb-options">
        {field.options.map((o, i) => (
          <div className="row" key={i}>
            <input type="text" value={o.label} disabled={!editable} placeholder="Label"
              onChange={(e) => {
                const options = [...field.options];
                options[i] = { ...o, label: e.target.value };
                onChange({ options });
              }} />
            <input type="text" value={o.value} disabled={!editable} placeholder="Stored value"
              onChange={(e) => {
                const options = [...field.options];
                options[i] = { ...o, value: e.target.value };
                onChange({ options });
              }} />
            <button className="small danger tight" disabled={!editable}
              onClick={() => onChange({ options: field.options.filter((_, j) => j !== i) })}>×</button>
          </div>
        ))}
        <button className="small" disabled={!editable}
          onClick={() => onChange({ options: [...field.options, { value: '', label: '' }] })}>
          Add an option
        </button>
      </div>
    </Field>
  );
}

const EIGHTH_SCHEDULE = [
  ['en', 'English'], ['hi', 'हिन्दी'], ['bn', 'বাংলা'], ['mr', 'मराठी'], ['te', 'తెలుగు'],
  ['ta', 'தமிழ்'], ['gu', 'ગુજરાતી'], ['ur', 'اردو'], ['kn', 'ಕನ್ನಡ'], ['ml', 'മലയാളം'],
  ['or', 'ଓଡ଼ିଆ'], ['pa', 'ਪੰਜਾਬੀ'], ['as', 'অসমীয়া'], ['mai', 'मैथिली'], ['sat', 'ᱥᱟᱱᱛᱟᱲᱤ'],
  ['ks', 'کٲشُر'], ['ne', 'नेपाली'], ['sd', 'سنڌي'], ['kok', 'कोंकणी'], ['doi', 'डोगरी'],
  ['mni', 'ꯃꯤꯇꯩꯂꯣꯟ'], ['brx', 'बड़ो'], ['sa', 'संस्कृतम्'],
] as const;

function LanguagesTab({
  formId, version, draft, variants, offered, editable, onSaved,
}: {
  formId: string;
  version: VersionRow;
  draft: Definition;
  variants: Loaded['variants'];
  offered: string[];
  editable: boolean;
  onSaved: () => void;
}) {
  const toast = useToast();
  const others = offered.filter((c) => c !== 'en');
  const [lang, setLang] = useState<string>(variants[0]?.lang ?? others[0] ?? 'hi');
  const [strings, setStrings] = useState<VariantStrings>({ fields: {}, settings: {}, errors: {} });
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);

  async function draftFromKnown() {
    setDrafting(true);
    try {
      const res = await api.post<{ strings: VariantStrings; covered: string[]; missing: string[] }>(
        `/v1/forms/${formId}/versions/${version.version}/variants/${lang}/draft`,
      );
      // Only empty boxes are filled; a translator's own words stay.
      const fields = { ...strings.fields };
      for (const [k, v] of Object.entries(res.strings.fields)) {
        fields[k] = { ...v, ...Object.fromEntries(Object.entries(fields[k] ?? {}).filter(([, x]) => x)) };
      }
      setStrings({
        fields,
        settings: { ...res.strings.settings, ...Object.fromEntries(Object.entries(strings.settings).filter(([, x]) => x)) },
        errors: { ...res.strings.errors, ...strings.errors },
      });
      toast(res.missing.length
        ? `${res.covered.length} filled from what the platform knows; ${res.missing.length} left for a translator. Review, then save.`
        : `All ${res.covered.length} fields filled. Review, then save.`, 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setDrafting(false);
    }
  }

  const existing = useMemo(() => variants.find((v) => v.lang === lang), [variants, lang]);

  useEffect(() => {
    setStrings(existing?.strings ?? { fields: {}, settings: {}, errors: {} });
  }, [existing, lang]);

  const rtl = ['ur', 'ks', 'sd'].includes(lang);
  const translatable = draft.fields.filter((f) => f.kind !== 'heading' || f.label);

  async function save() {
    setBusy(true);
    try {
      await api.put(`/v1/forms/${formId}/versions/${version.version}/variants/${lang}`, strings);
      toast(`${lang} saved.`, 'ok');
      onSaved();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Languages">
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <Field label="Language" help="Languages the organisation offers are listed first. Blank boxes fall back to English.">
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            {others.length > 0 && (
              <optgroup label="Offered">
                {others.map((code) => (
                  <option key={code} value={code}>
                    {EIGHTH_SCHEDULE.find(([c]) => c === code)?.[1] ?? code}{variants.some((v) => v.lang === code) ? ' · translated' : ''}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Not offered yet">
              {EIGHTH_SCHEDULE.filter(([c]) => c !== 'en' && !offered.includes(c)).map(([code, name]) => (
                <option key={code} value={code}>{name}{variants.some((v) => v.lang === code) ? ' · translated' : ''}</option>
              ))}
            </optgroup>
          </select>
        </Field>
        {editable && (
          <div className="tight" style={{ display: 'flex', gap: 8, paddingBottom: 14 }}>
            <button onClick={draftFromKnown} disabled={drafting} title="Fills from the interface pack, the purposes' own translations and a glossary of common labels.">
              {drafting ? 'Drafting…' : 'Draft from what we know'}
            </button>
            <button className="primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="small muted" style={{ marginBottom: 6 }}>
        {(() => { const done = translatable.filter((f) => strings.fields[f.key]?.label).length; return `${done} of ${translatable.length} fields translated.`; })()}
      </div>

      {rtl && (
        <div className="note">Right-to-left script. The form is rendered RTL.</div>
      )}

      <div className="table-scroll" style={{ marginTop: 14 }}>
        <table className="data">
          <thead>
            <tr>
              <th>Field</th>
              <th>English</th>
              <th>Translation</th>
            </tr>
          </thead>
          <tbody>
            {translatable.map((f) => (
              <tr key={f.key}>
                <td className="small">{FIELD_KINDS.find(([k]) => k === f.kind)?.[1] ?? f.kind}</td>
                <td className="small">
                  <div>{f.label}</div>
                  {f.help && <div className="muted">{f.help}</div>}
                </td>
                <td>
                  <input
                    type="text" dir={rtl ? 'rtl' : 'ltr'} disabled={!editable}
                    value={strings.fields[f.key]?.label ?? ''}
                    placeholder={f.label}
                    onChange={(e) => setStrings({
                      ...strings,
                      fields: {
                        ...strings.fields,
                        [f.key]: { ...strings.fields[f.key], label: e.target.value },
                      },
                    })}
                  />
                  {f.help && (
                    <input
                      type="text" dir={rtl ? 'rtl' : 'ltr'} disabled={!editable}
                      style={{ marginTop: 6 }}
                      value={strings.fields[f.key]?.help ?? ''}
                      placeholder={f.help}
                      onChange={(e) => setStrings({
                        ...strings,
                        fields: {
                          ...strings.fields,
                          [f.key]: { ...strings.fields[f.key], help: e.target.value },
                        },
                      })}
                    />
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td className="small">Submit button</td>
              <td className="small">{draft.settings.submitText}</td>
              <td>
                <input type="text" dir={rtl ? 'rtl' : 'ltr'} disabled={!editable}
                  value={strings.settings.submitText ?? ''} placeholder={draft.settings.submitText}
                  onChange={(e) => setStrings({
                    ...strings, settings: { ...strings.settings, submitText: e.target.value },
                  })} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ publish

function PublishTab({
  formId, form, version, dirty, writes, onPublished,
}: {
  formId: string;
  form: FormRow;
  version: VersionRow;
  dirty: boolean;
  writes: boolean;
  onPublished: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [how, setHow] = useState<'web' | 'app' | 'server' | 'json' | 'webhooks'>('web');
  const check = useResource<{ ok: boolean; problems: Array<{ field?: string; problem: string }> }>(
    () => api.get(`/v1/forms/${formId}/versions/${version.version}/check`),
    [formId, version.version, reload],
  );

  const point = useResource<{ collectionPoint: { id: string; apiKeyPrefix: string } }>(
    () => api.get(`/v1/collection-points/${form.collectionPointId}`),
  );

  async function publish() {
    setBusy(true);
    try {
      await api.post(`/v1/forms/${formId}/versions/${version.version}/publish`);
      toast(`v${version.version} is live.`, 'ok');
      onPublished();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setReload((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  const origin = window.location.origin;
  const keyPrefix = point.data?.collectionPoint.apiKeyPrefix ?? 'wkcp_…';
  const pointId = point.data?.collectionPoint.id ?? 'COLLECTION_POINT_ID';
  const fields = version.definition.fields.filter((f) => f.kind !== 'heading');
  const sample = (f: FormField): string | boolean => {
    if (f.kind === 'checkbox') return true;
    if (f.kind === 'email') return 'person@example.com';
    if (f.kind === 'phone') return '+919876543210';
    if (f.kind === 'date') return '2026-09-21';
    if (f.kind === 'dropdown' || f.kind === 'jurisdiction') return f.options[0]?.value ?? '';
    return '…';
  };
  const values = JSON.stringify(Object.fromEntries(fields.map((f) => [f.key, sample(f)])), null, 2)
    .split('\n').map((l, i) => (i === 0 ? l : '    ' + l)).join('\n');

  const webSnippet = `<div id="wekalp-consent"></div>
<script src="${origin}/widget.js"
        data-key="${keyPrefix}.YOUR_KEY"
        data-target="#wekalp-consent"
        data-lang="${version.definition.settings.defaultLang || 'en'}"></script>`;

  const appSnippet = `# 1. Fetch the form and notice text in the person's language
GET ${origin}/v1/widget/config?lang=hi
X-Consent-Key: ${keyPrefix}.YOUR_KEY

# 2. Post what the person filled in and ticked
POST ${origin}/v1/consents
X-Consent-Key: ${keyPrefix}.YOUR_KEY
Content-Type: application/json

{
  "lang": "hi",
  "form": { "values": ${values} }
}`;

  const serverSnippet = `POST ${origin}/v1/consents
X-Service-Key: wk_live_….YOUR_SERVICE_KEY
Content-Type: application/json

{
  "collectionPointId": "${pointId}",
  "lang": "en",
  "form": { "values": ${values} }
}

# Response 201: a receipt with one event per purpose, each with its hash
{ "principalHash": "…", "noticeVersion": 1, "contentHash": "…",
  "events": [{ "purposeId": "…", "action": "granted", "eventHash": "…" }],
  "requiredDeclined": [] }`;

  const webhookSnippet = `POST https://your-system.example/consent-events
X-Wekalp-Signature: t=1758400000,v1=<hmac-sha256 of "<t>.<body>">
Content-Type: application/json

{ "event": "withdrawn", "principalHash": "…", "purposeId": "…", "at": "2026-09-21T10:00:00Z" }`;

  const HOW: Array<{ id: typeof how; label: string; help: string }> = [
    { id: 'web', label: 'Web page', help: 'Paste once. The widget renders this form, in the languages it is complete in, and records each submission.' },
    { id: 'app', label: 'Mobile app', help: 'Render the form natively from its JSON, then post the answers with the same publishable key the web page uses.' },
    { id: 'server', label: 'Your own server or CRM', help: 'For consent taken elsewhere: a call centre script, a paper form keyed in, a CRM. Recorded as attested by you, not self-asserted by the person.' },
    { id: 'json', label: 'Form definition', help: 'The fields, validation and purposes of this version as JSON, for teams that build their own screen.' },
    { id: 'webhooks', label: 'Webhooks', help: 'Tell your systems when a decision changes. Signed, retried, logged under Processor notifications.' },
  ];

  return (
    <div className="grid cols-2" style={{ gridTemplateColumns: 'minmax(0, 360px) minmax(0, 1fr)', alignItems: 'start' }}>
      <div>
        <Card title="Publish">
          {check.loading && <Loading what="Checking" />}
          {check.data?.ok && (
            <div className="note ok">Checks passed. Publishing freezes and hashes this version.</div>
          )}
          {check.data && !check.data.ok && (
            <>
              <div className="note bad">Cannot publish:</div>
              <ul className="fb-problems">
                {check.data.problems.map((p, i) => (
                  <li key={i}>
                    {p.field && <strong>{version.definition.fields.find((f) => f.key === p.field)?.label ?? p.field}</strong>} {p.problem}
                  </li>
                ))}
              </ul>
            </>
          )}
          {dirty && (
            <div className="note warn" style={{ marginTop: 10 }}>Unsaved changes. Save the draft first.</div>
          )}
          {writes && version.status === 'draft' && (
            <div style={{ marginTop: 14 }}>
              <button className="primary" onClick={publish} disabled={busy || !check.data?.ok || dirty}>
                {busy ? 'Publishing…' : `Publish v${version.version}`}
              </button>
            </div>
          )}
          {version.status === 'published' && (
            <div className="small muted" style={{ marginTop: 10 }}>
              Published{version.publishedAt ? ` on ${new Date(version.publishedAt).toLocaleString()}` : ''}.
              Every submission quotes this version's content hash.
            </div>
          )}
        </Card>

        <Card title="Integration">
          {HOW.map((h) => (
            <button
              key={h.id}
              type="button"
              className="check-item"
              onClick={() => setHow(h.id)}
              aria-pressed={how === h.id}
              style={how === h.id ? { background: 'var(--surface-tint)' } : undefined}
            >
              <span className="check-body">
                <span className="check-text" style={{ fontWeight: how === h.id ? 600 : 400 }}>{h.label}</span>
                <span className="small muted">{h.help}</span>
              </span>
            </button>
          ))}
        </Card>
      </div>

      <div>
        {how === 'web' && (
          <Card title="Web page">
            <pre className="mono keyblock">{webSnippet}</pre>
            <div className="small muted">
              Uses the publishable key for this collection point ({keyPrefix}…), shown under Collection points › Embed code. It can only submit for this point; it cannot read anything back.
            </div>
          </Card>
        )}
        {how === 'app' && (
          <Card title="Mobile app">
            <pre className="mono keyblock">{appSnippet}</pre>
            <div className="small muted">
              The config call returns the notice text, the fields and the purposes for the language asked. Submissions are recorded as self-asserted.
            </div>
          </Card>
        )}
        {how === 'server' && (
          <Card title="Your own server or CRM">
            <pre className="mono keyblock">{serverSnippet}</pre>
            <div className="small muted">
              Needs a service key with "Record consent", issued under <Link to="/settings">Users &amp; access › Service keys</Link>. Values are keyed by the field names above; the field marked as identifier locates the person. The same key with "Check consent" answers <code className="mono">GET /v1/consents/&lt;identifier&gt;/&lt;purpose&gt;/check</code> before a message goes out.
            </div>
          </Card>
        )}
        {how === 'json' && (
          <Card title="Form definition">
            <div className="small" style={{ marginBottom: 10 }}>
              Version {version.version} of this form: fields in order, validation rules, the purpose each checkbox binds to, and the button text in each language.
            </div>
            <button
              className="primary"
              onClick={() => api.download(`/v1/forms/${formId}/preview?version=${version.version}`, `${form.key || 'form'}-v${version.version}.json`)
                .catch((err) => toast((err as Error).message, 'bad'))}
            >
              Download JSON
            </button>
            <table className="data" style={{ marginTop: 14 }}>
              <thead><tr><th>Field</th><th>Key in payload</th><th>Type</th><th>Purpose</th></tr></thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.key}>
                    <td>{f.label}{f.identifier && <> <Pill tone="brand">Identifier</Pill></>}{f.required && <> <Pill>Required</Pill></>}</td>
                    <td className="mono small">{f.key}</td>
                    <td className="small">{FIELD_KINDS.find(([k]) => k === f.kind)?.[1] ?? f.kind}</td>
                    <td className="small">{f.kind === 'checkbox' ? (f.purposeId ? 'Bound' : 'None') : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        {how === 'webhooks' && (
          <Card title="Webhooks">
            <div className="small" style={{ marginBottom: 10 }}>
              Events: <strong>granted</strong>, <strong>declined</strong>, <strong>withdrawn</strong>, <strong>expired</strong>, <strong>refreshed</strong>. Each delivery is signed, retried over two and a half days, and listed with its outcome.
            </div>
            <pre className="mono keyblock">{webhookSnippet}</pre>
            <div className="small muted">
              Subscriptions are per system, not per form: set them up under <Link to="/propagation">Processor notifications</Link>. A system that cannot receive calls can pull the suppression list with a service key instead.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
